// What the Repos screens compute from the daemon's answers — kept free of
// React so each rule can be tested against the Rust it mirrors.
//
// Three of these are **mirrors**, and say so where they are defined, because a
// mirror that drifts from its original is worse than none: it shows an
// operator a confident answer to a question the daemon answers differently.
//
//   - `consentClass` mirrors `git_ns::ops::consent_class`.
//   - `desiredTuples` mirrors `git_ns::projection::desired_all`, including
//     the implied `git.commit.sign` the projector writes explicitly because
//     verify-trust asks about that action and no other, and the role-derived
//     commit rights inside bound namespaces, which the projector now owns.
//   - `contains` mirrors `Resource::contains` — whole-segment containment, so
//     `github.com/acme` holds `github.com/acme/widgets` and not
//     `github.com/acme-labs/x`.
//
// One is **a report with a labelled fallback**: `guardFor` shows the guard the
// bridge reports in force on a repository (`guard`), and only where it has
// not reported one does it fall back to the guard design §9 assigns — labelled
// as expected, never as observed.

import type {
  GitNsBootstrapStatus,
  GitNsBreakGlassMark,
  GitNsDriftItem,
  GitNsNamespaceRow,
  GitNsPublishedRow,
  GitNsRepoRow,
  GitNsRight,
  GitNsRightRow,
  GitNsRoleMap,
} from "@/lib/wire-types";

export const RIGHTS: readonly GitNsRight[] = [
  "git.ns.admin",
  "git.repo.create",
  "git.repo.own",
  "git.repo.maintain",
  "git.commit.sign",
];

export const RIGHT_LABEL: Record<GitNsRight, string> = {
  "git.ns.admin": "Namespace admin",
  "git.repo.create": "Repo creator",
  "git.repo.own": "Owner",
  "git.repo.maintain": "Maintainer",
  "git.commit.sign": "Committer",
};

/** Strongest first — the order people are listed in on a repository. */
const RIGHT_RANK: Record<GitNsRight, number> = {
  "git.ns.admin": 0,
  "git.repo.create": 1,
  "git.repo.own": 2,
  "git.repo.maintain": 3,
  "git.commit.sign": 4,
};

export function isRight(value: string): value is GitNsRight {
  return (RIGHTS as readonly string[]).includes(value);
}

export function rightLabel(value: string): string {
  return isRight(value) ? RIGHT_LABEL[value] : value;
}

export function rightRank(value: string): number {
  return isRight(value) ? RIGHT_RANK[value] : 99;
}

/** The rights that go on a repository, as opposed to a namespace. */
export const REPO_RIGHTS: readonly GitNsRight[] = [
  "git.repo.own",
  "git.repo.maintain",
  "git.commit.sign",
];

// ── resources ───────────────────────────────────────────────────────────

/** Whole-segment containment. Mirrors `Resource::contains`. */
export function contains(parent: string, child: string): boolean {
  return child === parent || child.startsWith(`${parent}/`);
}

/** `github.com/acme/widgets` → `acme/widgets`: the name people say out loud.
 *  The forge host is dropped for display only; it stays in every link. */
export function shortName(resource: string): string {
  const i = resource.indexOf("/");
  return i < 0 ? resource : resource.slice(i + 1);
}

/** The last segment: `widgets`. */
export function leafName(resource: string): string {
  return resource.slice(resource.lastIndexOf("/") + 1);
}

// ── consent classes (design §6) ─────────────────────────────────────────

export type ConsentClass = "normal" | "elevated" | "destructive";

export type GitNsAction =
  | "namespace.bind"
  | "namespace.unbind"
  | "namespace.reseat"
  | "right.grant"
  | "right.revoke"
  | "repo.adopt"
  | "repo.transfer"
  | "repo.archive"
  | "repo.create"
  | "drift.resolve"
  | "right.breakGlass"
  | "right.ratify"
  | "roles.reproject";

/** Mirrors `git_ns::ops::consent_class`. */
export function consentClass(action: GitNsAction, right?: GitNsRight): ConsentClass {
  if (
    action === "namespace.bind" ||
    action === "namespace.unbind" ||
    action === "namespace.reseat" ||
    // `git-ns/right/break-glass` declares `sideEffects: destructive`: one
    // person widening their own authority. Gated by a passkey gesture bound
    // to the document rather than by `elevated_requires_admin`.
    action === "right.breakGlass"
  ) {
    return "destructive";
  }
  // Resolving drift is gated as the grant or revocation it amounts to —
  // adopt as `consent_gate("right.grant", right)` inside `right_grant_via`,
  // revert as `consent_gate("right.revoke", impact)` — so it is classed by
  // that right the same way.
  if (
    action === "right.grant" ||
    action === "right.revoke" ||
    action === "drift.resolve" ||
    // Ratifying makes a self-granted right count as an ordinary grant, so it
    // weighs as granting it.
    action === "right.ratify"
  ) {
    if (right === "git.ns.admin") return "destructive";
    if (right === "git.repo.own" || right === "git.repo.create") return "elevated";
    return "normal";
  }
  if (action === "repo.create" || action === "roles.reproject") return "normal";
  return "elevated";
}

// ── namespaces ──────────────────────────────────────────────────────────

export type Tone = "success" | "warning" | "danger" | "accent" | "neutral";

export interface Finding {
  tone: Tone;
  title: string;
  detail: string;
}

/**
 * What an administrator must know about a namespace before anything else.
 *
 * Only what the daemon reports: a pending binding, a lost installation, a
 * namespace with no admin, and — where the bridge has reported its standing
 * on the forge (`forgeStatus`, absent until it does) — permissions the App
 * lacks and a permission upgrade waiting on the owner. An absent report is
 * not read as "all permissions granted": nothing is said either way.
 */
export function namespaceFindings(ns: GitNsNamespaceRow): Finding[] {
  const out: Finding[] = [];
  const fs = ns.forgeStatus;
  if (ns.state === "pending") {
    out.push({
      tone: "accent",
      title: "Binding not finished",
      detail:
        "The VTC is waiting for the bridge to report the App installed on this owner. Nothing can be granted here until it does.",
    });
  }
  if (ns.installationRemoved) {
    out.push({
      tone: "danger",
      title: "The App lost access",
      detail:
        "The bridge reported the community's App uninstalled from this owner. Rights still stand and are still published, but nothing is applied on the forge and drift is no longer checked. Reinstall the App on the owner to restore it.",
    });
  }
  if (fs && fs.missingPermissions.length > 0) {
    out.push({
      tone: "danger",
      title: `The App is missing ${fs.missingPermissions.length === 1 ? "a permission" : "permissions"}`,
      detail: `The installation lacks ${fs.missingPermissions.join(", ")}. The bridge cannot do what needs ${fs.missingPermissions.length === 1 ? "it" : "them"} until the owner grants ${fs.missingPermissions.length === 1 ? "it" : "them"} in the App's installation settings.`,
    });
  }
  if (fs?.permissionUpgradePending) {
    out.push({
      tone: "warning",
      title: "Permission upgrade awaiting approval",
      detail:
        "A new bridge release asks for more permissions. The forge owner must approve the change on the App's installation page; until then the bridge works with the permissions it had.",
    });
  }
  if (ns.kind === "organization" && ns.mode === "bridge" && fs?.orgRulesets === false) {
    out.push({
      tone: "warning",
      title: "No org rulesets on this plan",
      detail:
        "The owner's plan cannot run the required workflow, so repositories here fall back to code-owner review and a bridge-posted check (design §9).",
    });
  }
  if (ns.headless) {
    out.push({
      tone: "danger",
      title: "No namespace admin",
      detail:
        "Its last admin left or lapsed, so nobody can grant here. A community administrator can reseat it with git-ns/namespace/reseat (cnm git reseat): namespace admin goes to a current member of the community, with a statement of why that is kept in the audit record and shown to the namespace's repository owners. It is refused while a current member holds a live git.ns.admin there, so it never goes around a sitting admin.",
    });
  }
  return out;
}

export function isPersonal(ns: GitNsNamespaceRow): boolean {
  return ns.kind === "user";
}

export function kindLabel(ns: GitNsNamespaceRow): string {
  if (ns.kind === "organization") return "Organization";
  if (ns.kind === "user") return "Personal account";
  return ns.state === "pending" ? "Kind not yet known" : "Kind not reported";
}

export function modeLabel(ns: GitNsNamespaceRow): string {
  if (ns.mode === "manual") return "Manual mode";
  if (ns.state === "pending") return "App install pending";
  return ns.installationRemoved ? "App uninstalled" : "App installed";
}

/** The bridge's own `git.commit.sign` on the namespace — the Dependabot
 *  re-sign service grant (design §9), recorded with `grantedBy` the VTC. */
export function isServiceGrant(right: GitNsRightRow, ns: GitNsNamespaceRow): boolean {
  return (
    !!ns.bridgeDid &&
    right.subject === ns.bridgeDid &&
    right.right === "git.commit.sign" &&
    right.resource === ns.resource &&
    right.origin === "recorded"
  );
}

// ── repositories ────────────────────────────────────────────────────────

export type RepoAction = "view" | "resolve" | "assignOwner" | "adopt";

export interface RepoStatus {
  label: string;
  tone: Tone;
  action?: RepoAction;
  actionLabel?: string;
}

const ACTION_LABEL: Record<RepoAction, string> = {
  view: "View",
  resolve: "Resolve",
  assignOwner: "Assign owner",
  adopt: "Adopt",
};

/**
 * One status per row, lifecycle first and forge sync second: a repository
 * being created or waiting to be adopted has no sync state worth reading yet.
 */
export function repoStatus(repo: GitNsRepoRow): RepoStatus {
  const withAction = (s: Omit<RepoStatus, "actionLabel">): RepoStatus => ({
    ...s,
    actionLabel: s.action ? ACTION_LABEL[s.action] : undefined,
  });
  switch (repo.state) {
    case "pendingCreate":
      return withAction(
        repo.failedStep
          ? { label: `Creating · failed at ${repo.failedStep}`, tone: "danger", action: "view" }
          : { label: "Creating", tone: "accent", action: "view" },
      );
    case "unmanaged":
      return withAction({ label: "Unmanaged", tone: "neutral", action: "adopt" });
    case "orphaned":
      return withAction({ label: "Orphaned", tone: "danger", action: "assignOwner" });
    case "archived":
      return withAction({ label: "Archived", tone: "neutral" });
    case "detached":
      return withAction({ label: "Detached", tone: "neutral" });
  }
  switch (repo.syncState) {
    case "inSync":
      return withAction({ label: "In sync", tone: "success" });
    case "drift":
      return withAction({
        label: `Drift · ${repo.driftCount} ${repo.driftCount === 1 ? "item" : "items"}`,
        tone: "warning",
        action: "resolve",
      });
    case "pending":
      return withAction({ label: "Applying on the forge", tone: "accent" });
    case "unchecked":
      return withAction({ label: "Not checked (manual)", tone: "neutral" });
  }
  return withAction({ label: repo.syncState, tone: "neutral" });
}

export interface BootstrapStep {
  key: keyof GitNsBootstrapStatus;
  label: string;
  detail: string;
  done: boolean;
}

/** The four steps that turn commit trust on, in the order the bridge takes
 *  them (design §5.3). A step the forge's plan does not need reads done. */
export function bootstrapSteps(b: GitNsBootstrapStatus): BootstrapStep[] {
  return [
    {
      key: "workflow",
      label: "Workflow",
      detail: "verify-trust runs on pull requests",
      done: b.workflow,
    },
    {
      key: "keyring",
      label: "Keyring",
      detail: "platform keyring for forge-signed merges",
      done: b.keyring,
    },
    {
      key: "variables",
      label: "Variables",
      detail: "TRUST_REGISTRY_DID · VTC_DID",
      done: b.variables,
    },
    {
      key: "requiredCheck",
      label: "Required check",
      detail: "merge blocked unless the check passes, no bypass",
      done: b.requiredCheck,
    },
  ];
}

/** What each step is, for the operator who has never seen the design. */
export const BOOTSTRAP_STEP_EXPLAINED: Record<keyof GitNsBootstrapStatus, string> = {
  workflow:
    "A forge Actions workflow (.github/workflows/verify-trust.yml) that runs verify-trust on every pull request and fails it when a commit is not signed by a DID holding git.commit.sign in the Trust Registry.",
  keyring:
    "The forge's own signing key (.github/trusted-platform-keys.asc), so verify-trust accepts the merge commits the forge signs when a pull request is merged in its web UI — only clean merges whose parents all verify.",
  variables:
    "Repository variables TRUST_REGISTRY_DID and VTC_DID: which Trust Registry verify-trust asks, and whose records count.",
  requiredCheck:
    "The ruleset making “Verify commit trust” a required check on the default branch with no bypass. This is the step that actually blocks merging untrusted commits.",
};

/** A step as it stands on one repository. `notApplicable`: this repository's
 *  setup does not use it, so it is neither in place nor missing. */
export type StepState = "done" | "missing" | "failed" | "notApplicable";

export interface BootstrapStepView extends BootstrapStep {
  state: StepState;
  /** Why the step is in that state, in a sentence. */
  why: string;
}

/**
 * Each bootstrap step on `repo`, with whether it applies here.
 *
 * The VTC records each step as a bare in-place flag, so a step the bridge's
 * plan never included reads exactly like a missing one. Two signals tell them
 * apart, and this reads both:
 *
 *  - **The guard.** Where verify-trust runs from the org's required workflow,
 *    or the bridge runs it and posts the check itself, the repository's own
 *    workflow file is not used (design §9: "the Actions workflow is then not
 *    needed in fallback mode").
 *  - **The bridge's last run.** A step reported `skipped`, or left out of a
 *    run that reported others, is outside the bridge's plan for this forge.
 *
 * A step the bridge reported `failed` is failed whatever the guard says.
 */
export function bootstrapView(ns: GitNsNamespaceRow, repo: GitNsRepoRow): BootstrapStepView[] {
  const guard = guardFor(ns, repo);
  const reported = new Map(repo.steps.map((s) => [s.step, s.outcome]));
  return bootstrapSteps(repo.bootstrap).map((s) => {
    const outcome = reported.get(s.key);
    if (s.done) return { ...s, state: "done", why: "In place, as the bridge last reported." };
    if (outcome === "failed") {
      return {
        ...s,
        state: "failed",
        why: "The bridge's last attempt at this step failed. Every step is check-then-apply, so a retry is safe.",
      };
    }
    if (s.key === "workflow" && guard.mode === "requiredWorkflow") {
      return {
        ...s,
        state: "notApplicable",
        why: "Not needed here: verify-trust runs from the organization's required workflow in the bridge-managed .vgi repository, not from a file in this repository.",
      };
    }
    if (s.key === "workflow" && guard.mode === "bridgePostedCheck") {
      return {
        ...s,
        state: "notApplicable",
        why: "Not needed here: the bridge runs verify-trust itself and posts the check under the community App's identity, so no workflow file is used.",
      };
    }
    if (outcome === "skipped") {
      return {
        ...s,
        state: "notApplicable",
        why: "Not needed here: the bridge skipped this step, as its plan for this forge does not use it.",
      };
    }
    if (repo.steps.length > 0 && outcome === undefined) {
      return {
        ...s,
        state: "notApplicable",
        why: "Not part of this repository's setup: the bridge's last run reported the other steps and not this one.",
      };
    }
    return {
      ...s,
      state: "missing",
      why: "Not in place, and the bridge has not said this repository does without it.",
    };
  });
}

export interface CommitTrustVerdict {
  tone: Tone;
  /** One line: is commit trust enforced? */
  headline: string;
  /** What that means, and what to do about it if anything. */
  detail: string;
}

/**
 * Commit trust on `repo` in one sentence: enforced, enforced with a caveat,
 * or not — and what, if anything, the operator should do.
 */
export function commitTrustVerdict(ns: GitNsNamespaceRow, repo: GitNsRepoRow): CommitTrustVerdict {
  const steps = bootstrapView(ns, repo);
  const guard = guardFor(ns, repo);
  const gaps = steps.filter((s) => s.state === "missing" || s.state === "failed");
  const check = steps.find((s) => s.key === "requiredCheck");
  if (check && (check.state === "missing" || check.state === "failed")) {
    return {
      tone: "danger",
      headline: "Not enforced: untrusted commits can be merged",
      detail:
        "The required check is not in place, so nothing on the forge blocks a merge. The bridge re-applies a weakened ruleset by default; if this persists, see Drift below and the bridge's recent jobs.",
    };
  }
  if (gaps.length > 0) {
    return {
      tone: "warning",
      headline: `Action needed: ${gaps.map((s) => s.label.toLowerCase()).join(" and ")} ${gaps.length === 1 ? "is" : "are"} ${gaps.some((s) => s.state === "failed") ? "failing" : "missing"}`,
      detail:
        "Merges are blocked unless the check passes, but the check may fail every pull request until this is fixed. The bridge retries on its next inspection; a failure reason is shown below when it gave one.",
    };
  }
  if (guard.tone === "danger") {
    return {
      tone: "danger",
      headline: "Not guaranteed",
      detail: `${guard.detail} Bind the namespace in bridge mode, or add the protection the guard names.`,
    };
  }
  if (guard.tone === "warning") {
    return { tone: "warning", headline: `Enforced, with a gap: ${guard.label.toLowerCase()}`, detail: guard.detail };
  }
  if (guard.tone === "accent") {
    return {
      tone: "accent",
      headline: "Enforced, relying on reviewers",
      detail: `${guard.detail} Nothing to do unless you need a stronger guarantee.`,
    };
  }
  const na = steps.filter((s) => s.state === "notApplicable").length;
  return {
    tone: "success",
    headline: "Enforced: only commits signed by authorized DIDs can merge",
    detail: `Every step this repository uses is in place${na > 0 ? ` (${na} of ${steps.length} do not apply to its setup)` : ""}, and ${guard.label.toLowerCase()} stops a pull request from passing its own check. Nothing to do.`,
  };
}

/** The dots' label: what is in place, missing, failed and not applicable. */
export function bootstrapViewSummary(steps: BootstrapStepView[]): string {
  const of = (state: StepState) => steps.filter((s) => s.state === state);
  const names = (list: BootstrapStepView[]) => list.map((s) => s.label.toLowerCase()).join(", ");
  const done = of("done");
  const na = of("notApplicable");
  const gaps = [...of("missing"), ...of("failed")];
  if (gaps.length === 0 && na.length === 0) return "All four in place";
  if (done.length === 0 && gaps.length > 0) return "Not bootstrapped";
  if (gaps.length === 0) {
    return `All that apply in place; ${names(na)} not applicable`;
  }
  return [
    done.length > 0 ? `${done.map((s) => s.label).join(", ")} in place` : null,
    of("missing").length > 0 ? `${names(of("missing"))} missing` : null,
    of("failed").length > 0 ? `${names(of("failed"))} failed` : null,
    na.length > 0 ? `${names(na)} not applicable` : null,
  ]
    .filter(Boolean)
    .join("; ");
}

export function bootstrapSummary(b: GitNsBootstrapStatus): string {
  const steps = bootstrapSteps(b);
  const done = steps.filter((s) => s.done);
  if (done.length === steps.length) return "All four in place";
  if (done.length === 0) return "Not bootstrapped";
  return `${done.map((s) => s.label).join(", ")} in place; ${steps
    .filter((s) => !s.done)
    .map((s) => s.label.toLowerCase())
    .join(", ")} missing`;
}

// ── the guard (design §9) ───────────────────────────────────────────────

export type GuardMode =
  | "unknown"
  | "requiredWorkflow"
  | "bridgePostedCheck"
  | "codeOwnerReview"
  | "protectedFiles"
  | "soloUnreviewed"
  | "none";

export interface Guard {
  mode: GuardMode;
  label: string;
  detail: string;
  /** `reported` — the bridge said so; `expected` — design §9 for this
   *  namespace, because the bridge has not reported. */
  source: "reported" | "expected";
  tone: Tone;
}

const GUARD: Record<Exclude<GuardMode, "unknown">, Omit<Guard, "mode" | "source">> = {
  requiredWorkflow: {
    label: "Required workflow",
    detail:
      "An org ruleset runs verify-trust from the bridge-managed .vgi repository at a pinned commit, so a pull request cannot change what runs.",
    tone: "success",
  },
  bridgePostedCheck: {
    label: "Bridge-posted check",
    detail:
      "The bridge runs verify-trust itself and posts the check with the community App's identity, which the ruleset pins; no workflow can forge it.",
    tone: "success",
  },
  codeOwnerReview: {
    label: "Owner review",
    detail:
      "CODEOWNERS assigns .github/ to the repository's owners and the ruleset requires their review, so a workflow change needs a second owner. Writers are trusted not to post a forged check.",
    tone: "accent",
  },
  protectedFiles: {
    label: "Protected workflow files",
    detail:
      "Branch protection refuses any pull request that touches a workflow file; such changes go through a namespace admin's direct, audited push. Writers are trusted not to forge a commit status.",
    tone: "accent",
  },
  soloUnreviewed: {
    label: "Solo owner — workflow changes unreviewed",
    detail:
      "Code-owner review needs a second owner to approve. With one, the owner cannot merge their own workflow change; add a co-owner.",
    tone: "warning",
  },
  none: {
    label: "None",
    detail:
      "Nothing stops a pull request from changing what its own check runs, so a writer could make it pass. Commit trust is not guaranteed here.",
    tone: "danger",
  },
};

const REPORTED: Record<string, Exclude<GuardMode, "unknown" | "soloUnreviewed">> = {
  requiredWorkflow: "requiredWorkflow",
  bridgePostedCheck: "bridgePostedCheck",
  codeOwnerReview: "codeOwnerReview",
  protectedFiles: "protectedFiles",
  none: "none",
};

/**
 * The guard that stops a pull request satisfying its own check (design §9).
 *
 * The bridge's report (`repo.guard`) when there is one. Code-owner review
 * with a single owner is reported as what it is in practice — nobody reviews.
 * A report this console does not know is shown verbatim, as reported, and
 * never replaced by a guess. With no report, the guard §9 assigns for this
 * namespace, marked `expected` — and the org-wide required workflow is only
 * expected where the bridge has not said it is unavailable.
 */
export function guardFor(ns: GitNsNamespaceRow, repo: GitNsRepoRow): Guard {
  const solo = repo.owners.length <= 1;
  if (repo.guard) {
    const known = REPORTED[repo.guard];
    if (!known) {
      return {
        mode: "unknown",
        source: "reported",
        label: repo.guard,
        detail:
          "The bridge reported a guard this console does not recognise. It is shown as reported; check the bridge's documentation for what it means.",
        tone: "neutral",
      };
    }
    const mode = known === "codeOwnerReview" && solo ? "soloUnreviewed" : known;
    return { mode, source: "reported", ...GUARD[mode] };
  }
  const fs = ns.forgeStatus;
  const orgWorkflow =
    ns.mode === "bridge" &&
    ns.kind === "organization" &&
    fs?.orgRulesets !== false &&
    fs?.requiredWorkflow !== false;
  let mode: Exclude<GuardMode, "unknown">;
  if (orgWorkflow) {
    mode = "requiredWorkflow";
  } else if (solo) {
    mode = "soloUnreviewed";
  } else if (ns.mode === "bridge") {
    mode = "bridgePostedCheck";
  } else {
    mode = "codeOwnerReview";
  }
  return { mode, source: "expected", ...GUARD[mode] };
}

/** The last verify-trust check the bridge saw. Carried as an object whose
 *  documented members are `{conclusion, concludedAt, sha?}`; anything else is ignored. */
export function lastCheckOf(
  repo: GitNsRepoRow,
): { conclusion: string; concludedAt?: string; sha?: string } | null {
  const c = repo.lastCheck as Record<string, unknown> | null | undefined;
  if (!c || typeof c.conclusion !== "string") return null;
  return {
    conclusion: c.conclusion,
    concludedAt: typeof c.concludedAt === "string" ? c.concludedAt : undefined,
    sha: typeof c.sha === "string" ? c.sha : undefined,
  };
}

// ── the registry projection ─────────────────────────────────────────────

export interface Tuple {
  entity: string;
  action: string;
  resource: string;
  /** The right this tuple is implied by (`git.repo.own` → `git.commit.sign`). */
  impliedBy?: string;
  /** Whether the daemon reports this exact tuple published. */
  published: boolean;
}

const tupleKey = (t: { entity: string; action: string; resource: string }) =>
  `${t.entity}\u0000${t.action}\u0000${t.resource}`;

/**
 * What the projector should publish. Mirrors `git_ns::projection::desired_all`:
 * only bound namespaces; only repositories whose state publishes (active,
 * orphaned, archived); an archived repository's commit rights withdrawn,
 * records and implications alike; the implied `git.commit.sign` of every
 * `own`, `maintain` and `ns.admin` written explicitly; and a role-derived
 * (`grant_on_role`) commit right on any resource inside a bound namespace —
 * the projection publishes those there, and the hook relay only outside one.
 */
export function desiredTuples(
  rights: GitNsRightRow[],
  namespaces: GitNsNamespaceRow[],
  repos: GitNsRepoRow[],
  published: GitNsPublishedRow[],
): Tuple[] {
  const have = new Set(published.map(tupleKey));
  const out = new Map<string, Tuple>();
  const add = (entity: string, action: string, resource: string, impliedBy?: string) => {
    const key = tupleKey({ entity, action, resource });
    const existing = out.get(key);
    // An explicit record wins over an implication, as `merge` keeps the
    // record's own context.
    if (existing && !existing.impliedBy) return;
    out.set(key, { entity, action, resource, impliedBy, published: have.has(key) });
  };
  for (const r of rights) {
    if (r.origin === "roleDerived") {
      const bound = namespaces.some((n) => n.state === "bound" && contains(n.resource, r.resource));
      if (bound) add(r.subject, "git.commit.sign", r.resource);
      continue;
    }
    if (r.origin !== "recorded") continue;
    const ns = namespaces.find((n) => contains(n.resource, r.resource));
    if (!ns || ns.state !== "bound") continue;
    const repo = repos.find((x) => x.resource === r.resource);
    if (r.resource !== ns.resource) {
      if (!repo || !["active", "orphaned", "archived"].includes(repo.state)) continue;
    }
    const archived = repo?.state === "archived";
    if (!(archived && r.right === "git.commit.sign")) add(r.subject, r.right, r.resource);
    const impliesCommit =
      r.right === "git.repo.own" ||
      r.right === "git.repo.maintain" ||
      r.right === "git.ns.admin";
    if (impliesCommit && !archived) add(r.subject, "git.commit.sign", r.resource, r.right);
  }
  return [...out.values()].sort(
    (a, b) =>
      a.resource.localeCompare(b.resource) ||
      a.entity.localeCompare(b.entity) ||
      rightRank(a.action) - rightRank(b.action),
  );
}

// ── people on a repository ──────────────────────────────────────────────

/** Is the grant past its `expiresAt`, or within `days` of it? */
export function expiresWithin(right: GitNsRightRow, days: number, now = Date.now()): boolean {
  if (!right.expiresAt) return false;
  const at = new Date(right.expiresAt).getTime();
  return !Number.isNaN(at) && at - now <= days * 86_400_000;
}

/** Rights on exactly this repository, strongest first, then by subject. */
export function repoRights(rights: GitNsRightRow[], resource: string): GitNsRightRow[] {
  return rights
    .filter((r) => r.resource === resource)
    .sort(
      (a, b) => rightRank(a.right) - rightRank(b.right) || a.subject.localeCompare(b.subject),
    );
}

/** Namespace-level rights that reach into this repository. */
export function inheritedRights(
  rights: GitNsRightRow[],
  ns: GitNsNamespaceRow,
): GitNsRightRow[] {
  return rights
    .filter((r) => r.resource === ns.resource)
    .filter((r) => r.right === "git.ns.admin" || r.right === "git.commit.sign")
    .sort(
      (a, b) => rightRank(a.right) - rightRank(b.right) || a.subject.localeCompare(b.subject),
    );
}

// ── drift ───────────────────────────────────────────────────────────────

const ROLE_DRIFT: readonly GitNsDriftItem["type"][] = ["roleAdded", "roleRemoved", "roleChanged"];

/** Whether a drift item is about one account's role (selected by account). */
export function isRoleDrift(item: GitNsDriftItem): boolean {
  return ROLE_DRIFT.includes(item.type);
}

// ── the bridge's role map ───────────────────────────────────────────────

/** The bridge's forge-neutral role ladder, lowest first. */
export const FORGE_LEVELS = ["none", "read", "triage", "write", "maintain", "admin"] as const;

function level(role: string | undefined): number {
  return role === undefined ? -1 : (FORGE_LEVELS as readonly string[]).indexOf(role);
}

/**
 * The forge role `right` projects to under `map` — mirrors
 * `RoleMap::role_for`. A namespace admin (and a repo creator) gets no forge
 * role, whatever the map.
 */
export function forgeRoleFor(map: GitNsRoleMap, right: string): string {
  switch (right) {
    case "git.repo.own":
      return map.own;
    case "git.repo.maintain":
      return map.maintain;
    case "git.commit.sign":
      return map.commit;
    default:
      return "none";
  }
}

/**
 * The right the bridge projects to `role` — mirrors
 * `git_ns::drift::projected_right`: the **lowest** right whose role in the
 * bridge's map is `role`. `none`, or a role no right's is, has none. Under
 * the default map on an organisation `admin` is `git.repo.own` and
 * `maintain` `git.repo.maintain`; on a personal account `write` is
 * `git.repo.maintain`, the lower of the two it collapses.
 */
export function projectedRight(map: GitNsRoleMap, role: string | undefined): GitNsRight | null {
  if (role === undefined || role === "none" || level(role) < 0) return null;
  for (const right of ["git.commit.sign", "git.repo.maintain", "git.repo.own"] as const) {
    if (forgeRoleFor(map, right) === role) return right;
  }
  return null;
}

/**
 * The revocation a revert amounts to, which is what the VTC gates it as —
 * mirrors `git_ns::drift::revert`: taking off or lowering a forge role at or
 * above the one `own` projects to weighs as revoking `own`; any other revert
 * at most as revoking `maintain`. With no map reported (`map` absent) any
 * role but `none` might be the one `own` projects to, so every such revert
 * weighs as revoking `own` — mirrors `role_map::revert_takes_ownership`.
 */
export function driftRevertImpact(
  item: GitNsDriftItem,
  map: GitNsRoleMap | null | undefined,
): "git.repo.own" | "git.repo.maintain" {
  if (!isRoleDrift(item) || item.type === "roleRemoved") return "git.repo.maintain";
  if (!map) return level(item.observed) > 0 ? "git.repo.own" : "git.repo.maintain";
  return map.own !== "none" && level(item.observed) >= level(map.own)
    ? "git.repo.own"
    : "git.repo.maintain";
}

/** What reverting the item has the bridge do, in the operator's words. */
export function driftRevertEffect(item: GitNsDriftItem): string {
  switch (item.type) {
    case "roleAdded":
      return "The bridge takes the forge role off the account, so the repository's roles match the VTC's projection again. No VTC right changes.";
    case "roleRemoved":
    case "roleChanged":
      return "The bridge re-applies the projected roles, so the account holds the level its VTC right calls for again. No VTC right changes.";
    case "requiredCheckMissing":
    case "protectionWeakened":
      return "The bridge re-applies the ruleset, so “Verify commit trust” is required again and the protection is as the VTC projects it. No VTC right changes.";
    default:
      return "The bridge re-runs the bootstrap plan, which restores only what is missing (workflow, keyring, variables, required check). No VTC right changes.";
  }
}

export type RevertStanding =
  | { may: true }
  | { may: false; why: string };

/**
 * Whether the VTC would accept a revert of `item` signed as `viewer` — the
 * same checks `git-ns/drift/resolve` makes, in its order, as far as the
 * console can see them:
 *
 * - the namespace has a bridge to undo anything (`notRevertible` otherwise);
 * - the repository is active or orphaned (`repoNotActive`);
 * - the signer holds `git.repo.own` there, explicit or implied by the
 *   namespace's `git.ns.admin` (`drift_revert_admitted`);
 * - where the revert weighs as revoking `own`, the signer is a community
 *   administrator (`consent_gate`, under `[git_ns] elevated_requires_admin`,
 *   which is on by default and which the console assumes, as it does for
 *   every elevated task).
 *
 * The VTC decides either way; this only keeps the console from offering what
 * it would refuse.
 */
export function revertStanding(
  viewer: string | null,
  superAdmin: boolean,
  ns: GitNsNamespaceRow,
  repo: GitNsRepoRow,
  item: GitNsDriftItem,
): RevertStanding {
  if (ns.mode !== "bridge" || !ns.bridgeDid) {
    return {
      may: false,
      why: "This namespace is governed in manual mode: no bridge can undo a forge-side change, so fix it on the forge by hand.",
    };
  }
  if (repo.state !== "active" && repo.state !== "orphaned") {
    return { may: false, why: `The repository is ${repo.state}; drift is resolved on an active or orphaned one.` };
  }
  const owns = !!viewer && (repo.owners.includes(viewer) || ns.admins.includes(viewer));
  if (!owns) {
    return {
      may: false,
      why: "Reverting drift is an owner's decision: it needs git.repo.own on the repository, which its owners and the namespace's admins hold. This session's DID holds neither, so hand the command below to one of them.",
    };
  }
  if (driftRevertImpact(item, repo.roleMap) === "git.repo.own" && !superAdmin) {
    return {
      may: false,
      why: "Taking an admin role off the forge weighs as revoking ownership, an elevated action this VTC accepts only from a community administrator who also owns the repository. Hand the command below to one.",
    };
  }
  return { may: true };
}

/** Whether the item is one adopt can take at all (`git_ns::drift::adopt`,
 *  step 1): a role added or raised on the forge. */
export function isAdoptableKind(item: GitNsDriftItem): boolean {
  return item.type === "roleAdded" || item.type === "roleChanged";
}

/**
 * The member's *projected right* on the repository as a rank
 * (`Right::rank`: own 3, maintain 2, commit.sign 1; 0 for none) — what
 * drift/resolve 0.3 adopt step 5 compares a `roleChanged` against, and what
 * `bridge::projected_repo_right` computes: the highest right recorded **in
 * the member's own name** that reaches the repository — own, maintain or
 * commit.sign on it (its explicit owners, `repo.owners`, included), or
 * commit.sign on its namespace. `git.ns.admin` projects to no forge role, so
 * what it implies does not count: a namespace admin holding `maintain` here
 * ranks 2, and can adopt a forge `admin` as owner. Expired rows count for
 * nothing, and `roleDerived` rows — v0.1 hook-relay grants merged into the
 * listing from the registry, never held in the git-ns store — are ignored.
 */
export function projectedRepoRank(
  rights: GitNsRightRow[],
  member: string,
  repo: GitNsRepoRow,
  ns: GitNsNamespaceRow,
  now = Date.now(),
): number {
  let best = repo.owners.includes(member) ? 3 : 0;
  for (const r of rights) {
    if (r.subject !== member) continue;
    if (r.origin === "roleDerived") continue;
    if (r.expiresAt && Date.parse(r.expiresAt) <= now) continue;
    if (r.resource === repo.resource) {
      if (r.right === "git.repo.own") best = Math.max(best, 3);
      else if (r.right === "git.repo.maintain") best = Math.max(best, 2);
      else if (r.right === "git.commit.sign") best = Math.max(best, 1);
    } else if (r.resource === ns.resource && r.right === "git.commit.sign") {
      // A namespace-wide commit.sign is projected on each repository.
      best = Math.max(best, 1);
    }
  }
  return best;
}

const RANK: Partial<Record<GitNsRight, number>> = {
  "git.repo.own": 3,
  "git.repo.maintain": 2,
  "git.commit.sign": 1,
};

/**
 * Whether `right` is elevated on a repository under its role map — mirrors
 * `Right::is_elevated_in`: an elevated right, or one the map projects to
 * forge `admin` (a map that gives maintainers `admin` makes
 * `git.repo.maintain` elevated). With no map reported, `git.repo.maintain`
 * counts too, which fails closed.
 */
export function isElevatedIn(right: GitNsRight, map: GitNsRoleMap | null | undefined): boolean {
  if (isElevated(right)) return true;
  if (!map) return right === "git.repo.maintain";
  return forgeRoleFor(map, right) === "admin";
}

export type AdoptStanding =
  /** Adoptable, by this viewer: grants `right` to `member`. `selfGrant`
   *  when that is the viewer adopting an elevated right for themselves,
   *  offered only under single-administrator mode, whose waiver the VTC
   *  applies if nobody else could adopt it. */
  | { may: true; member: string; right: GitNsRight; selfGrant?: boolean }
  /** Adoptable, but not by this viewer — hand the command to someone who can. */
  | { may: false; handOver: true; member: string; right: GitNsRight; why: string }
  /** Not adoptable by anyone as it stands. */
  | { may: false; handOver: false; why: string };

/**
 * Whether the VTC would accept adopting `item` signed as `viewer` — the
 * checks `git-ns/drift/resolve` makes for `adopt`, in its order, as far as
 * the console can see them:
 *
 * - the repository is active or orphaned (`repoNotActive`);
 * - the signer holds `git.repo.own` there, explicit or implied
 *   (`permissionDenied`);
 * - step 1: a role added or raised on the forge (`notAdoptable`);
 * - step 2: the account is linked to a member (`accountNotLinked`; whether
 *   that member is still current the VTC decides);
 * - step 3: a right projects to the observed role (`noMatchingRight`)
 *   under the bridge's reported role map (`projectedRight`); with no map
 *   reported nothing is adoptable (`git-ns:roleMapUnknown`);
 * - step 5 (0.3): a `roleChanged` adopts only a raise over the member's
 *   projected right (`projectedRepoRank`) — a lowering is accepted by
 *   revoking, not adopting (`notAdoptable`);
 * - step 6 (0.3): separation of duties — nobody adopts an elevated right
 *   (`git.repo.own`, or one the role map projects to forge `admin`:
 *   `isElevatedIn`) for themselves (`git-ns:selfGrantNotAllowed`); the
 *   viewer is the DID a console key acts as, so this is its admin. Another
 *   owner can adopt it, so the command is handed over rather than offered;
 * - step 7: the grant's consent class — `own` is elevated, which this VTC
 *   accepts only from a community administrator (`elevated_requires_admin`,
 *   assumed on, as for every elevated task).
 *
 * Under single-administrator mode (`singleAdmin`, VTI-APV-022) step 6 is not
 * applied here: the VTC waives it for the one administrator when nobody else
 * could adopt the role, and refuses it otherwise, so the console sends it and
 * lets the VTC decide.
 *
 * The VTC decides either way; this only keeps the console from offering what
 * it would refuse.
 */
export function adoptStanding(
  viewer: string | null,
  superAdmin: boolean,
  ns: GitNsNamespaceRow,
  repo: GitNsRepoRow,
  item: GitNsDriftItem,
  member: string | undefined,
  heldRank: number,
  singleAdmin = false,
): AdoptStanding {
  const refuse = (why: string): AdoptStanding => ({ may: false, handOver: false, why });
  if (repo.state !== "active" && repo.state !== "orphaned") {
    return refuse(`The repository is ${repo.state}; drift is resolved on an active or orphaned one.`);
  }
  if (!isAdoptableKind(item)) {
    return refuse("Only a role added or raised on the forge can be adopted; revert this one, or revoke with a right change.");
  }
  if (!member) {
    return refuse("No member has linked this forge account, so there is nobody to grant it to. It can only be reverted.");
  }
  if (!repo.roleMap) {
    return refuse(
      "The bridge has not reported its role map, so which git right this forge role stands for is unknown: it cannot be adopted until the bridge reports (`git-ns:roleMapUnknown`).",
    );
  }
  const right = projectedRight(repo.roleMap, item.observed);
  if (!right) {
    return refuse(
      `No git right projects to the forge role "${item.observed ?? "nothing"}" here. Revert it, or grant a right and then revert the role.`,
    );
  }
  if (item.type === "roleChanged" && (RANK[right] ?? 0) <= heldRank) {
    return refuse(
      `The forge shows ${item.observed}, no higher than the member is projected at here — that is a lowering, accepted by revoking the right, not by adopting.`,
    );
  }
  // Separation of duties, mirroring `rules::elevated_on` (grant 0.3 rule 7,
  // drift/resolve 0.3 step 6): where the bridge's map projects the right to
  // forge `admin` it is elevated too — but break-glass does not carry it.
  const selfGrant = !!viewer && viewer === member.trim() && isElevatedIn(right, repo.roleMap);
  if (selfGrant && !singleAdmin) {
    return {
      may: false,
      handOver: true,
      member,
      right,
      why: isElevated(right)
        ? `Adopting this role would make you ${rightLabel(right).toLowerCase()}, an elevated right nobody grants themselves (separation of duties). Hand the command below to another owner or namespace admin — or, if nobody else can, use break-glass (git-ns/right/break-glass), which every other administrator sees until one ratifies or revokes it.`
        : `Adopting this role would make you ${rightLabel(right).toLowerCase()}, which the bridge's role map gives the forge's admin role here, so it is an elevated right nobody grants themselves (separation of duties). Hand the command below to another owner or namespace admin.`,
    };
  }
  const owns = !!viewer && (repo.owners.includes(viewer) || ns.admins.includes(viewer));
  if (!owns) {
    return {
      may: false,
      handOver: true,
      member,
      right,
      why: "Adopting is an owner's decision too: hand the command below to an owner or namespace admin.",
    };
  }
  if (consentClass("drift.resolve", right) !== "normal" && !superAdmin) {
    return {
      may: false,
      handOver: true,
      member,
      right,
      why: `Adopting as ${rightLabel(right).toLowerCase()} is an elevated grant, which this VTC accepts only from a community administrator who also owns the repository. Hand the command below to one.`,
    };
  }
  return selfGrant ? { may: true, member, right, selfGrant: true } : { may: true, member, right };
}

// ── activity ────────────────────────────────────────────────────────────

const ACTIVITY: Record<string, string> = {
  "gitNs.namespace.bindRequested": "binding requested",
  "gitNs.namespace.bound": "namespace bound",
  "gitNs.namespace.bindFailed": "binding failed",
  "gitNs.namespace.bindExpired": "binding expired unfinished",
  "gitNs.namespace.unbound": "namespace unbound",
  "gitNs.namespace.installationRemoved": "App uninstalled from the owner",
  "gitNs.repo.reserved": "repository name reserved",
  "gitNs.repo.activated": "repository activated",
  "gitNs.repo.adopted": "repository adopted",
  "gitNs.repo.archived": "repository archived",
  "gitNs.repo.detached": "repository detached",
  "gitNs.repo.orphaned": "repository orphaned — its last owner left",
  "gitNs.repo.renamed": "repository renamed",
  "gitNs.repo.transferred": "ownership transferred",
  "gitNs.repo.protectionWeakened": "protection weakened on the forge",
  "gitNs.right.granted": "granted",
  "gitNs.right.revoked": "revoked",
  "gitNs.right.lapsed": "lapsed",
  "gitNs.drift.reported": "drift reported",
  "gitNs.drift.resolved": "drift resolved",
  "gitNs.account.linked": "forge account linked",
  "gitNs.right.breakGlass": "broke the glass — self-granted",
  "gitNs.right.breakGlassRatified": "break-glass ratified",
  "gitNs.right.breakGlassRevoked": "break-glass revoked",
  "gitNs.right.selfGrantWaived": "self-granted — single-administrator mode",
  "gitNs.pullRequest.closed": "pull request closed — its author may not open one here",
  "gitNs.job.closePullRequest": "bridge job close pull request",
  "gitNs.pullRequest.gateUnenforced": "pull-request policy not enforced — the bridge cannot close pull requests (needs git-ns/bridge/job 0.6)",
};

/** A bridge job's kind in words, for the job list
 *  (`git-ns/bridge/job/list/0.4`): a `closePullRequest` job names the pull
 *  request it closes. */
export function jobKindLabel(job: { kind: string; number?: number }): string {
  switch (job.kind) {
    case "closePullRequest":
      return job.number !== undefined ? `close pull request #${job.number}` : "close pull request";
    case "projectRoles":
      return "project roles";
    case "createRepo":
      return "create repository";
    case "beginBind":
      return "begin binding";
    case "beginAccountLink":
      return "begin account link";
    default:
      return job.kind;
  }
}

/** An activity item's action in words. Unknown actions are shown verbatim
 *  rather than dropped: a new audit action is still something that happened. */
export function activityVerb(action: string): string {
  if (ACTIVITY[action]) return ACTIVITY[action];
  if (action.startsWith("gitNs.job.")) return `bridge job ${action.slice("gitNs.job.".length)}`;
  return action;
}

// ── break-glass (git-ns/right/break-glass, ratify) ──────────────────────
//
// Separation of duties (`git-ns/right/grant` 0.3, fixed rule 7): nobody grants
// themselves an *elevated* right. The explicit self-grant is break-glass,
// which takes effect at once, never expires, and stays flagged in front of
// every administrator until another one ratifies or revokes it.

/** The rights separation of duties covers. Mirrors `_shared/0.4`
 *  `ElevatedRight`. */
export const ELEVATED_RIGHTS: readonly GitNsRight[] = [
  "git.ns.admin",
  "git.repo.create",
  "git.repo.own",
];

export function isElevated(right: string): boolean {
  return (ELEVATED_RIGHTS as readonly string[]).includes(right);
}

/** Would granting `right` to `subject`, signed as `viewer`, be a self-grant
 *  the VTC refuses with `git-ns:selfGrantNotAllowed`? */
export function isSelfGrant(viewer: string | null, subject: string, right: string): boolean {
  return !!viewer && viewer === subject.trim() && isElevated(right);
}

// ── single-administrator mode's self-grant waiver (VTI-APV-022) ─────────
//
// On a node in single-administrator mode, where nobody else could make the
// grant, the VTC waives separation of duties for that one operation instead
// of refusing it — on the requester's passkey, audited at Critical. The record
// is marked; `git-ns/view` lists it under `ext.org.openvtc.selfGrantWaived`.

/** One record made under the waiver, as `git-ns/view` lists it. */
export interface GitNsSelfGrantWaived {
  subject: string;
  right: string;
  resource: string;
  /** When it was recorded. */
  at: string;
  /** The task that recorded it (`git-ns/right/grant/0.4`, …). */
  task: string;
}

/** The waived records a `git-ns/view` answer's `ext` lists; anything that is
 *  not one is dropped. */
export function selfGrantWaivedOf(
  ext: { "org.openvtc"?: { selfGrantWaived?: unknown } } | undefined,
): GitNsSelfGrantWaived[] {
  const list = ext?.["org.openvtc"]?.selfGrantWaived;
  if (!Array.isArray(list)) return [];
  return list.filter(
    (w): w is GitNsSelfGrantWaived =>
      !!w &&
      typeof w === "object" &&
      typeof (w as GitNsSelfGrantWaived).subject === "string" &&
      typeof (w as GitNsSelfGrantWaived).right === "string" &&
      typeof (w as GitNsSelfGrantWaived).resource === "string",
  );
}

/** The waiver mark on a recorded right, if it was made under one. */
export function waiverFor(
  waived: readonly GitNsSelfGrantWaived[] | undefined,
  row: { subject: string; right: string; resource: string; origin?: string },
): GitNsSelfGrantWaived | null {
  if (!waived || row.origin === "roleDerived") return null;
  return (
    waived.find(
      (w) => w.subject === row.subject && w.right === row.right && w.resource === row.resource,
    ) ?? null
  );
}

export type BreakGlassState = "unratified" | "pending" | "ratified";

/**
 * One break-glass record, as the console lists it: a `git-ns/view/0.6`
 * record carrying `breakGlass`, with the namespace it is in and its state
 * now (`api.ts`, `breakGlassItems`). A console view model, not a wire shape.
 */
export interface GitNsBreakGlassItem {
  /** The namespace's identifier; empty if the answer did not carry it. */
  namespace: string;
  /** The namespace's resource (`github.com/acme`). */
  namespaceResource: string;
  subject: string;
  right: string;
  resource: string;
  grantedAt: string;
  breakGlass: GitNsBreakGlassMark;
  state: BreakGlassState;
}

export interface GitNsBreakGlassList {
  items: GitNsBreakGlassItem[];
}

/** A break-glass mark's state now. `pending` — a policy delay has not run
 *  out, so the right confers nothing yet; `unratified` — live and flagged. */
export function breakGlassState(
  mark: GitNsBreakGlassMark | null | undefined,
  now = Date.now(),
): BreakGlassState | null {
  if (!mark) return null;
  if (mark.ratifiedBy) return "ratified";
  if (mark.effectiveAt) {
    const at = new Date(mark.effectiveAt).getTime();
    if (!Number.isNaN(at) && at > now) return "pending";
  }
  return "unratified";
}

/** Unratified or pending: what the banner counts. */
export function awaitsDecision(state: BreakGlassState | string | null | undefined): boolean {
  return state === "unratified" || state === "pending";
}

export const BREAK_GLASS_STATE_LABEL: Record<BreakGlassState, string> = {
  unratified: "Break-glass · unratified",
  pending: "Break-glass · delayed",
  ratified: "Break-glass · ratified",
};

/**
 * Whether a record counts toward the last-owner and last-admin invariants —
 * mirrors fixed rules 3 and 4 of `git-ns/right/grant` 0.3: no `expiresAt`,
 * and not an unratified break-glass record.
 */
export function countsTowardInvariant(row: GitNsRightRow, now = Date.now()): boolean {
  if (row.expiresAt) return false;
  return !awaitsDecision(breakGlassState(row.breakGlass, now));
}

/** Items still waiting for another administrator, oldest first. */
export function awaitingItems(items: GitNsBreakGlassItem[]): GitNsBreakGlassItem[] {
  return items
    .filter((i) => awaitsDecision(i.state))
    .sort((a, b) => a.breakGlass.recordedAt.localeCompare(b.breakGlass.recordedAt));
}

export type Standing = { may: true } | { may: false; why: string };

/**
 * Whether the VTC would accept `viewer` ratifying `item` — mirrors
 * `git-ns/right/ratify` 0.1, *Authorization*, as far as the console can see:
 *
 * - never the record's subject (`selfRatification`);
 * - a community administrator; or
 * - an authority over the right on the resource by a right that is not
 *   itself an unratified break-glass: a namespace admin of its namespace, or
 *   for `git.repo.own`, an owner of the repository.
 *
 * `rights` are the console's live right rows, to tell the viewer's own
 * standing apart from a break-glass of theirs nobody has confirmed.
 */
export function ratifyStanding(
  viewer: string | null,
  superAdmin: boolean,
  item: GitNsBreakGlassItem,
  rights: GitNsRightRow[],
): Standing {
  if (!viewer) return { may: false, why: "Signed-in DID unknown." };
  if (viewer === item.subject) {
    return {
      may: false,
      why: "You broke this glass yourself. A break-glass is ratified by someone else or not at all — ask another administrator to ratify it, or revoke it yourself if it is no longer needed.",
    };
  }
  if (item.state === "ratified") return { may: false, why: "Already ratified." };
  if (superAdmin) return { may: true };
  const confirmed = (r: GitNsRightRow) =>
    r.subject === viewer && r.origin === "recorded" && !awaitsDecision(breakGlassState(r.breakGlass));
  const nsAdmin = rights.some(
    (r) => confirmed(r) && r.right === "git.ns.admin" && r.resource === item.namespaceResource,
  );
  const owner =
    item.right === "git.repo.own" &&
    rights.some((r) => confirmed(r) && r.right === "git.repo.own" && r.resource === item.resource);
  if (nsAdmin || owner) return { may: true };
  return {
    may: false,
    why: "Ratifying needs a community administrator, or an administrator of this resource whose own standing is not an unconfirmed break-glass. Hand the command below to one.",
  };
}

/**
 * Whether the VTC would accept `viewer` revoking `item` — mirrors
 * `git-ns/right/revoke` 0.3: the subject resigning, any community
 * administrator (for an unratified record), a namespace admin of its
 * namespace, or an owner of the repository for `own`.
 */
export function revokeBreakGlassStanding(
  viewer: string | null,
  superAdmin: boolean,
  item: GitNsBreakGlassItem,
  rights: GitNsRightRow[],
): Standing {
  if (!viewer) return { may: false, why: "Signed-in DID unknown." };
  if (viewer === item.subject) return { may: true };
  if (superAdmin && awaitsDecision(item.state)) return { may: true };
  const holds = (right: string, resource: string) =>
    rights.some((r) => r.subject === viewer && r.origin === "recorded" && r.right === right && r.resource === resource);
  if (holds("git.ns.admin", item.namespaceResource)) return { may: true };
  if (item.right === "git.repo.own" && holds("git.repo.own", item.resource)) return { may: true };
  return {
    may: false,
    why: superAdmin
      ? "Ratified, it is an ordinary grant: its namespace admins revoke it. Hand the command below to one."
      : "Revoking it needs a community administrator or an administrator of this resource. Hand the command below to one.",
  };
}
